// src/utils/entitlementErrors.ts
//
// Phase A5.8 — user-facing copy for provider-entitlement runtime errors.
//
// The CRM voice paths surface Outbound's stable error CODES verbatim
// (`circleVoiceTokenController` responds `{ error, code, message }`,
// where `code` is the Outbound reason such as PROVIDER_NOT_ENTITLED).
// Before this module the UI rendered the generic `error` string —
// "Failed to mint Circle voice token" — which tells the caller nothing
// about WHY, and in the entitlement case actively misleads: it reads
// like a system fault when it is actually a deliberate authorization
// decision with a clear remedy ("ask your Team Head").
//
// Contract:
//   • The CODE stays the machine-readable truth and is still returned
//     by the API and written to logs — this only changes presentation.
//   • Unknown codes fall back to the server-supplied message, then to a
//     generic string, so a new Outbound reason never renders as blank.

/** Stable Outbound/Central entitlement reasons the UI gives copy for. */
export const ENTITLEMENT_ERROR_MESSAGES: Record<string, string> = {
  // Outbound authorizeResolvedProvider → strict policy, no active grant.
  PROVIDER_NOT_ENTITLED:
    "You don't have access to this calling provider. Ask your Team Head for provider access.",
  // Outbound could not reach Central, or the caller forwarded no Central
  // bearer. Fail-closed by design — transient, so invite a retry.
  PROVIDER_ENTITLEMENT_UNAVAILABLE:
    'Provider access could not be verified right now. Please try again.',
  // Central grant-authoring rejections (Head-only surfaces).
  INSUFFICIENT_AUTHORITY:
    'Only the Head of this Team can change provider access.',
  PROVIDER_NOT_KNOWN: 'That provider is not available in this workspace.',
  PROVIDER_INACTIVE:
    'That provider is not active. Ask a workspace administrator to re-enable it.',
  PROVIDER_CHANNEL_MISMATCH:
    'That provider does not support this channel.',
  PROVIDER_WORKSPACE_MISMATCH:
    'That provider belongs to a different workspace.',
  BAD_CHANNEL: 'Unsupported channel.',
};

const GENERIC_FALLBACK = 'Something went wrong. Please try again.';

/**
 * Resolve the message to show a user for an entitlement-related failure.
 *
 * `code` wins over `serverMessage` deliberately: the server's message is
 * written for operators ("Failed to mint Circle voice token"), while the
 * code carries the actual decision. Falling back to the server message
 * only when the code is unrecognised keeps new upstream reasons legible
 * instead of collapsing them to the generic string.
 */
export function entitlementErrorMessage(
  code?: string | null,
  serverMessage?: string | null,
): string {
  if (code && ENTITLEMENT_ERROR_MESSAGES[code]) {
    return ENTITLEMENT_ERROR_MESSAGES[code];
  }
  if (serverMessage && serverMessage.trim().length > 0) return serverMessage;
  return GENERIC_FALLBACK;
}

/** True when the failure is an entitlement decision rather than a fault. */
export function isEntitlementDenial(code?: string | null): boolean {
  return code === 'PROVIDER_NOT_ENTITLED';
}

/**
 * Pull `{ code, message, error }` out of an RTK Query error payload.
 * Shapes vary (`err.data.code`, `err.data.error`), so normalise once
 * here rather than at every call site.
 */
export function readEntitlementError(err: unknown): {
  code: string | null;
  message: string;
} {
  const data = (err as { data?: unknown } | undefined)?.data as
    | { code?: unknown; error?: unknown; message?: unknown }
    | undefined;

  const code =
    typeof data?.code === 'string'
      ? data.code
      : typeof data?.error === 'string' && ENTITLEMENT_ERROR_MESSAGES[data.error]
        ? data.error
        : null;

  const serverMessage =
    typeof data?.message === 'string' && data.message
      ? data.message
      : typeof data?.error === 'string'
        ? data.error
        : err instanceof Error
          ? err.message
          : null;

  return { code, message: entitlementErrorMessage(code, serverMessage) };
}
